import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eatDayOf, formatEatTime, formatIsoDay, SCHEDULE_MAX_DAYS_AHEAD } from '@wodi/domain';
import type { ImagingExamView } from '@wodi/shared';
import { Button, DateField, Dialog, DialogActions, Notice, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { performImagingExam, scheduleImagingExam, setImagesLink } from '../api/imaging.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { eatInstant } from './format.ts';
import styles from './imaging.module.css';

// The exam's dialogs (docs/plans/phase2-radiology.md §6): schedule, mark performed, and
// the images link placeholder. Each answers with the exam as it now is.

function useExamUpdate(exam: ImagingExamView) {
  const queryClient = useQueryClient();
  return async (next: ImagingExamView) => {
    queryClient.setQueryData(['imaging', 'exam', exam.orderId], next);
    await queryClient.invalidateQueries({ queryKey: ['imaging', 'worklist'] });
  };
}

const LINK_HELP = 'Paste the viewer address from the imaging system, starting with https://.';

export function ScheduleDialog({ exam, onClose }: { exam: ImagingExamView; onClose: () => void }) {
  const online = useOnline();
  const update = useExamUpdate(exam);
  const now = new Date();
  const current = exam.scheduledFor === null ? null : new Date(exam.scheduledFor);
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(current ?? now)));
  const [time, setTime] = useState(current === null ? '' : formatEatTime(current));
  const [note, setNote] = useState(exam.scheduleNote ?? '');
  const [tried, setTried] = useState(false);
  const at = day === null ? null : eatInstant(day, time);
  const save = useMutation({
    mutationFn: () =>
      scheduleImagingExam(exam.orderId, {
        scheduledFor: at?.toISOString() ?? '',
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: async (next) => {
      await update(next);
      toasts.show({ title: current === null ? 'Exam scheduled' : 'Time changed' });
      onClose();
    },
  });
  const latest = formatIsoDay(eatDayOf(new Date(now.getTime() + SCHEDULE_MAX_DAYS_AHEAD * 864e5)));
  return (
    <Dialog
      title={current === null ? 'Schedule exam' : 'Change time'}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {exam.preparation === null ? null : (
          <Notice tone="info">{`Preparation: ${exam.preparation}`}</Notice>
        )}
        <DateField
          label="Day"
          value={day}
          onChange={setDay}
          minValue={formatIsoDay(eatDayOf(now))}
          maxValue={latest}
          isRequired
          {...(tried && day === null ? { errorMessage: 'Choose the day.' } : {})}
        />
        <TextField
          label="Time"
          description="24-hour clock, for example 14:05."
          width="sm"
          value={time}
          onChange={setTime}
          isRequired
          {...(tried && at === null ? { errorMessage: 'Type the time as HH:MM, like 14:05.' } : {})}
        />
        <TextField
          label="Note (optional)"
          description="The room, or what the patient was told."
          value={note}
          onChange={setNote}
          maxLength={200}
        />
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The time couldn't be saved.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (at !== null) save.mutate();
          }}
        >
          {current === null ? 'Schedule exam' : 'Change time'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PerformDialog({ exam, onClose }: { exam: ImagingExamView; onClose: () => void }) {
  const online = useOnline();
  const update = useExamUpdate(exam);
  const [note, setNote] = useState('');
  const [link, setLink] = useState('');
  const save = useMutation({
    mutationFn: () =>
      performImagingExam(exam.orderId, {
        note: note.trim() === '' ? null : note.trim(),
        imagesLink: link.trim() === '' ? null : link.trim(),
      }),
    onSuccess: async (next) => {
      await update(next);
      toasts.show({ title: 'Exam performed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Mark performed"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p className={styles.meta}>{`${exam.itemName} for ${exam.patient.displayName}, now.`}</p>
        <TextArea
          label="Radiographer's note (optional)"
          description="Views taken, technique, anything the reporter should know."
          value={note}
          onChange={setNote}
          rows={3}
        />
        <TextField
          label="Link to the images (optional)"
          description={LINK_HELP}
          value={link}
          onChange={setLink}
          type="url"
        />
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The exam couldn't be marked done.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Mark performed
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ImagesLinkDialog({
  exam,
  onClose,
}: {
  exam: ImagingExamView;
  onClose: () => void;
}) {
  const online = useOnline();
  const update = useExamUpdate(exam);
  const [link, setLink] = useState(exam.imagesLink ?? '');
  const save = useMutation({
    mutationFn: () => setImagesLink(exam.orderId, link.trim() === '' ? null : link.trim()),
    onSuccess: async (next) => {
      await update(next);
      toasts.show({ title: next.imagesLink === null ? 'Images link removed' : 'Images linked' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Link to the images"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <TextField
          label="Link to the images"
          description={`${LINK_HELP} Leave it empty to remove the link.`}
          value={link}
          onChange={setLink}
          type="url"
        />
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The link couldn't be saved.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save link
        </Button>
      </DialogActions>
    </Dialog>
  );
}
